import {CommonModule} from '@angular/common';
import {Component, ElementRef, effect, input, model, output, viewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {Textarea} from 'primeng/textarea';
import {TooltipModule} from 'primeng/tooltip';

export interface CommentUserInfo {
  firstName: string;
  lastName: string;
}

export interface CommentWithUser {
  userId: number | null | undefined;
  comment: string;
  createdAt: string | Date;
  user?: CommentUserInfo | null;
}

@Component({
  selector: 'app-comment-modal',
  imports: [
    CommonModule,
    TranslocoPipe,
    DialogModule,
    ButtonModule,
    Textarea,
    FormsModule,
    TooltipModule,
  ],
  templateUrl: './comment-modal.component.html',
  styleUrl: 'comment-modal.component.scss',
})
export class CommentModalComponent {
  readonly visible = model(false);
  readonly comments = input<CommentWithUser[]>([]);
  readonly currentUserId = input<number | null | undefined>(null);
  readonly readOnly = input(false);
  readonly titleKey = input('comments.title');
  readonly noCommentsKey = input('comments.noComments');
  readonly placeholderKey = input('comments.placeholder');
  readonly sendLabelKey = input('comments.send');
  readonly save = output<string>();

  readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');

  newComment = '';

  private previousVisible = false;
  private previousComments: CommentWithUser[] | null = null;

  constructor() {
    effect(() => {
      const isVisible = this.visible();
      const currentComments = this.comments();
      const hasOpened = isVisible && !this.previousVisible;
      const hasUpdatedComments =
        isVisible && this.previousVisible && currentComments !== this.previousComments;

      if (hasOpened) {
        this.newComment = '';
        // Scroll to bottom when opening
        setTimeout(() => this.scrollToBottom(), 100);
      }

      // Scroll to bottom when comments are updated (e.g., after saving)
      if (hasUpdatedComments) {
        setTimeout(() => this.scrollToBottom(), 100);
      }

      this.previousVisible = isVisible;
      this.previousComments = currentComments;
    });
  }

  close() {
    this.visible.set(false);
    this.newComment = '';
  }

  sendComment() {
    if (this.newComment.trim()) {
      this.save.emit(this.newComment.trim());
      this.newComment = '';
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  onEnterKey(event: KeyboardEvent) {
    if (event.shiftKey) {
      // Allow shift+enter for new line
      return;
    }
    event.preventDefault();
    this.sendComment();
  }

  formatDateTime(dateValue: string | Date): string {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // If today, show time only
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    }

    // If yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday ' + date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    }

    // If within last week, show day name
    if (diffInSeconds < 7 * 24 * 60 * 60) {
      return (
        date.toLocaleDateString([], {weekday: 'short'}) +
        ' ' +
        date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
      );
    }

    // Otherwise show full date
    return (
      date.toLocaleDateString([], {day: '2-digit', month: '2-digit', year: 'numeric'}) +
      ' ' +
      date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
    );
  }

  private scrollToBottom() {
    const container = this.messagesContainer()?.nativeElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }
}
